import React, { useState, useEffect } from 'react';
import Portal from '../common/Portal';
import CustomSelect from '../common/CustomSelect';
import DatePicker from 'react-datepicker';

const LivestockFormModal = ({ isOpen, onClose, onSubmit, isLoading, initialData, livestockTypes }) => {
    const isEditMode = !!initialData;
    
    const getInitialState = () => {
        if (isEditMode) return { ...initialData };
        // Add new fields to the initial blank state
        return { name: '', tagNumber: '', breed: '', dateOfBirth: '', type: '', milkPrice: '', mother: '', father: '', breedingMethod: 'Natural' };
    };

    const [livestockData, setLivestockData] = useState(getInitialState());

    useEffect(() => {
        if (isOpen) {
            setLivestockData(getInitialState());
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => setLivestockData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleTypeChange = (selectedValue) => setLivestockData(prev => ({ ...prev, type: selectedValue }));
    const handleDateChange = (date) => {
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        setLivestockData(prev => ({ ...prev, dateOfBirth: formattedDate }));
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(livestockData);
    };

    const typeOptions = livestockTypes.map(type => ({ id: type.id, name: type.name, value: type.name }));
    const selectedDate = livestockData.dateOfBirth ? new Date(livestockData.dateOfBirth) : null;

    return (
        <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800">{isEditMode ? 'Edit Animal Details' : 'Add New Animal'}</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                            <input type="text" name="name" id="name" value={livestockData.name} onChange={handleChange} className="mt-1 block w-full form-input rounded-md shadow-sm" required />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Type</label>
                            <CustomSelect options={typeOptions} selected={livestockData.type} onChange={handleTypeChange} placeholder="Select a type..." />
                        </div>

                        <div>
                            <label htmlFor="tagNumber" className="block text-sm font-medium text-gray-700">Tag Number</label>
                            <input type="text" name="tagNumber" id="tagNumber" value={livestockData.tagNumber} onChange={handleChange} className="mt-1 block w-full form-input rounded-md shadow-sm" required />
                        </div>

                        <div>
                            <label htmlFor="breed" className="block text-sm font-medium text-gray-700">Breed</label>
                            <input type="text" name="breed" id="breed" value={livestockData.breed} onChange={handleChange} className="mt-1 block w-full form-input rounded-md shadow-sm" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateChange}
                                dateFormat="yyyy-MM-dd"
                                className="mt-1 block w-full form-input rounded-md shadow-sm"
                                wrapperClassName="w-full"
                                maxDate={new Date()}
                                showYearDropdown
                                scrollableYearDropdown
                                portalId="portal-root"
                                // This is the fix: apply our new CSS class to the calendar popup
                                popperClassName="high-z-datepicker"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="milkPrice" className="block text-sm font-medium text-gray-700">Milk Price/Liter (₹)</label>
                            <input type="number" step="0.01" name="milkPrice" id="milkPrice" value={livestockData.milkPrice || ''} onChange={handleChange} className="mt-1 block w-full form-input rounded-md shadow-sm" />
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-lg font-medium text-gray-900">Optional Details</h3>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <label htmlFor="mother" className="block text-sm font-medium text-gray-700">Mother (Dam)</label>
                                    <input type="text" name="mother" id="mother" value={livestockData.mother || ''} onChange={handleChange} placeholder="e.g., Tag # or Name" className="mt-1 block w-full form-input rounded-md shadow-sm" />
                                </div>
                                <div>
                                    <label htmlFor="father" className="block text-sm font-medium text-gray-700">Father (Sire)</label>
                                    <input type="text" name="father" id="father" value={livestockData.father || ''} onChange={handleChange} placeholder="e.g., Tag # or Name" className="mt-1 block w-full form-input rounded-md shadow-sm" />
                                </div>
                                <div>
                                    <label htmlFor="breedingMethod" className="block text-sm font-medium text-gray-700">Conception Method</label>
                                    <select name="breedingMethod" id="breedingMethod" value={livestockData.breedingMethod || 'Natural'} onChange={handleChange} className="mt-1 block w-full form-select rounded-md shadow-sm">
                                        <option>Natural</option>
                                        <option>Artificial Insemination (AI)</option>
                                        <option>Embryo Transfer</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="pt-5 flex justify-end space-x-3">
                            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 flex items-center">
                                {isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>}
                                {isLoading ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Animal')}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </Portal>
    );
};

export default LivestockFormModal;